import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { QRCode } from 'react-qrcode-logo';
import logo from '../assets/logo.png';   // fallback page/QR logo
import './CSS/Login.css';

// ------- QR + Logo sizing -------
const QR_SIZE = 250;              // px
const LOGO_MAX_FRACTION = 0.24;   // longest logo side ≈ 24% of QR
const COST_PER_GENERATE = 1;      // credits (deducted on Generate)

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const fitLogoToQR = (dataUrl, qrSize = QR_SIZE, maxFraction = LOGO_MAX_FRACTION) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const maxSide = Math.floor(qrSize * maxFraction);
      // keep aspect ratio; never upscale
      const scale = Math.min(maxSide / img.width, maxSide / img.height, 1);
      const targetW = Math.max(1, Math.round(img.width * scale));
      const targetH = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetW, targetH);
      resolve({ src: canvas.toDataURL('image/png'), width: targetW, height: targetH });
    };
    img.onerror = () => reject(new Error('Could not process the image.'));
    img.src = dataUrl;
  });

const QRCodeGenerator = (props) => {
  const [url, setUrl] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [customLogo, setCustomLogo] = useState(null);          // resized dataURL
  const [logoDims, setLogoDims] = useState({ w: 70, h: 70 });  // fallback for default logo
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // user meta
  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState(0);
  const [isCharging, setIsCharging] = useState(false);

  // Load user details from router state OR backend
  useEffect(() => {
    const state = props.location && props.location.state;
    if (state && state.email && state.balance !== undefined) {
      setEmail(state.email);
      setBalance(Number(state.balance) || 0);
    } else {
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async () => {
    try {
      const res = await fetch('/user-details');
      const data = await res.json();
      setEmail(data.email);
      setBalance(Number(data.balance || 0));
    } catch (err) {
      console.error('Error fetching user data', err);
    }
  };

  // Confirm, DEDUCT 1 CREDIT, then generate
  const handleGenerateQRCode = async () => {
    setError('');
    setNotice('');
    const trimmed = url.trim();
    if (!trimmed) {
      setError('Please enter a URL.');
      return;
    }
    if (balance < COST_PER_GENERATE) {
      setError('Not enough credits to generate the QR.');
      return;
    }

    const newBalance = balance - COST_PER_GENERATE;
    const ok = window.confirm(
      `Generate QR now?\nThis will deduct ${COST_PER_GENERATE} credit.\n` +
      `Current balance: ${balance.toFixed(2)} → After: ${newBalance.toFixed(2)}`
    );
    if (!ok) return;

    setIsCharging(true);
    try {
      const res = await fetch('/update-balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, newBalance }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to deduct credits.');
      }
      setBalance(newBalance);
      setQrCodeUrl(trimmed);
      setNotice(`✅ Deducted ${COST_PER_GENERATE} credit. New balance: ${newBalance.toFixed(2)}.`);
    } catch (e) {
      setError(e.message || 'Failed to deduct credits.');
    } finally {
      setIsCharging(false);
    }
  };

  const handleLogoUpload = async (e) => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Please upload a PNG or JPEG image.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('File too large. Max 4 MB.');
      return;
    }

    try {
      const dataUrl = await readFileAsDataURL(file);
      const { src, width, height } = await fitLogoToQR(dataUrl, QR_SIZE, LOGO_MAX_FRACTION);
      setCustomLogo(src);
      setLogoDims({ w: width, h: height });
    } catch {
      setError('Failed to process the image.');
    }
  };

  const clearCustomLogo = () => {
    setCustomLogo(null);
    setLogoDims({ w: 70, h: 70 });
  };

  // Download PNG (FREE)
  const handleConfirmAndDownload = () => {
    setError('');
    const canvas =
      document.getElementById('user-qr-code') ||
      document.querySelector('.qr-output canvas');

    if (!canvas) {
      setError('QR image not ready yet. Please try again.');
      return;
    }

    const dataURL = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = 'qr_code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="qr-container d-flex align-items-center justify-content-center">
      <div className="text-center" style={{ width: '100%' }}>
        {/* Page header logo (not the one inside QR) */}
        <img className="logo" src={logo} alt="Logo" />
        <h2>Generate QR Code for a URL</h2>

        {/* User meta */}
        <div className="mb-2">
          <strong>Email:</strong> {email || '—'} &nbsp;|&nbsp; <strong>Balance:</strong> {balance.toFixed(2)} credits
        </div>

        <div className="row justify-content-center">
          <div className="col-sm-8">
            <div className="card">
              <div className="card-body">
                {/* URL input */}
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter URL here"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />

                {/* Upload a custom logo */}
                <div className="mt-3 text-start">
                  <label className="form-label">Custom logo (PNG or JPEG)</label>
                  <input
                    type="file"
                    accept="image/png, image/jpeg"
                    className="form-control"
                    onChange={handleLogoUpload}
                  />
                  {customLogo && (
                    <div className="d-flex align-items-center gap-3 mt-2">
                      <img
                        src={customLogo}
                        alt="Uploaded preview"
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                      />
                      <button className="btn btn-outline-secondary btn-sm" onClick={clearCustomLogo}>
                        Remove custom logo
                      </button>
                    </div>
                  )}
                </div>

                {error && <div className="text-danger mt-2 small">{error}</div>}
                {notice && <div className="alert alert-success mt-2 p-2">{notice}</div>}

                <button
                  onClick={handleGenerateQRCode}
                  className="btn btn-dark mt-3"
                  disabled={isCharging || balance < COST_PER_GENERATE}
                  title={balance < COST_PER_GENERATE ? 'Not enough credits' : 'Deduct 1 credit and generate'}
                >
                  {isCharging ? 'Generating…' : `Generate QR Code (−${COST_PER_GENERATE} credit)`}
                </button>

                {qrCodeUrl && (
                  <div className="mt-4">
                    <h4>QR Code</h4>
                    <div className="qr-output d-inline-block">
                      <QRCode
                        id="user-qr-code"               // helps us find the canvas for download
                        value={qrCodeUrl}
                        size={QR_SIZE}
                        logoImage={customLogo || logo}
                        logoWidth={logoDims.w}
                        logoHeight={logoDims.h}
                        ecLevel="H"
                        removeQrCodeBehindLogo
                        qrStyle="dots"
                        eyeRadius={10}
                        enableCORS
                      />
                    </div>
                    <p className="small mb-2">
                      1 credit is deducted when you click <strong>Generate</strong>. Download is free.
                    </p>
                    <button
                      onClick={handleConfirmAndDownload}
                      className="btn btn-success"
                      title="Download PNG (free)"
                    >
                      Download PNG
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="text-start mt-3 small text-muted">
              Tip: If your URL is very long/dense please use smaller logo.
              ถ้า URL ของท่านมีขนาดยาวมากกรุณาใช้ภาพโลโก้ที่เล็กลงเพื่อคุณภาพการแสกนที่ดีขึ้น
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(QRCodeGenerator);
